<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main Content -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

    <!-- Topbar -->
    <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
      <HrTopbar
        :page-title="currentPageTitle"
        :is-sidebar-open="isSidebarOpen"
        :is-dark-mode="isDarkMode"
        @toggle-sidebar="toggleSidebar"
        @toggle-dark-mode="toggleDarkMode"
      />
    </header>

    <!-- Page Content -->
    <div class="p-6 mt-16">

      <!-- Top Bar -->
      <div class="flex justify-between items-center mb-8 p-4 bg-white dark:bg-gray-800">

        <input
          v-model="search"
          type="text"
          placeholder="Search permission..."
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-secondary dark:bg-gray-700 dark:text-white"
        />

        <button
          @click="openCreate"
          class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow flex items-center gap-2"
        >
          + Add Permission
        </button>

      </div>

      <!-- Permissions Grid -->
      <div v-if="filteredPermissions.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <div
          v-for="perm in filteredPermissions"
          :key="perm.id"
          class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
        >

          <div class="flex items-center justify-between mb-4">
            <div>
              <span class="inline-block px-3 py-1 text-xs font-medium bg-secondary text-white rounded-full">
                {{ perm.module_name }}
              </span>
            </div>
          </div>

          <h3 class="font-semibold text-gray-800 dark:text-white text-lg mb-2">
            {{ perm.name }}
          </h3>

          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {{ perm.label }}
          </p>

          <div class="flex justify-end gap-4 text-sm">
            <button
              @click="editPermission(perm)"
              class="text-blue-600 hover:underline flex items-center gap-1"
            >
              <i class="fas fa-edit"></i> Edit
            </button>
            <button
              @click="deletePermission(perm.id)"
              class="text-red-500 hover:underline flex items-center gap-1"
            >
              <i class="fas fa-trash"></i> Delete
            </button>
          </div>

        </div>

      </div>

      <!-- Empty State -->
      <div v-else class="text-center text-gray-400 mt-20 py-12">
        <p class="text-xl">No permissions found</p>
      </div>

      <!-- Modal -->
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[440px] shadow-lg">

            <h3 class="text-lg font-semibold text-[#035a76] mb-6">
              {{ editing ? 'Edit Permission' : 'Create New Permission' }}
            </h3>

            <form @submit.prevent="savePermission" class="space-y-5">

              <!-- Module Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Module
                </label>
                <select
                  v-model="form.module_id"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                >
                  <option value="" disabled>Select Module</option>
                  <option 
                    v-for="mod in modules" 
                    :key="mod.id" 
                    :value="mod.id"
                  >
                    {{ mod.name }}
                  </option>
                </select>
              </div>

              <!-- Permission Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Permission Name (e.g. view, create_applicant)
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  placeholder="Permission key"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div class="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  @click="closeModal"
                  class="px-5 py-2 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="px-5 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
                >
                  Save Permission
                </button>
              </div>

            </form>

          </div>
        </div>
      </Transition>

    </div>
  </main>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Permissions")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)
const search = ref("")

const permissions = ref([])
const modules = ref([])        // For dropdown

const form = ref({
  id: null,
  module_id: "",
  name: ""
})

// Fetch all permissions
const fetchPermissions = async () => {
  try {
    const res = await api.get("Permissions.php")
    permissions.value = res.data
  } catch (error) {
    console.error("Error fetching permissions:", error)
  }
}

// Fetch modules for dropdown
const fetchModules = async () => {
  try {
    const res = await api.get("Modules.php")
    modules.value = res.data
  } catch (error) {
    console.error("Error fetching modules:", error)
  }
}

const filteredPermissions = computed(() => {
  return permissions.value.filter(p =>
    p.name.toLowerCase().includes(search.value.toLowerCase()) ||
    p.module_name.toLowerCase().includes(search.value.toLowerCase())
  )
})

const openCreate = () => {
  editing.value = false
  form.value = { id: null, module_id: "", name: "" }
  showModal.value = true
}

const editPermission = (perm) => {
  editing.value = true
  form.value = {
    id: perm.id,
    module_id: perm.module_id,
    name: perm.name
  }
  showModal.value = true
}

const savePermission = async () => {
  try {
    if (editing.value) {
      await api.put("Permissions.php", form.value)
    } else {
      await api.post("Permissions.php", form.value)
    }

    closeModal()
    fetchPermissions()   // Refresh list
  } catch (error) {
    console.error("Error saving permission:", error)
    alert("Failed to save permission")
  }
}

const deletePermission = async (id) => {
  if (!confirm("Delete this permission?")) return

  try {
    await api.delete("Permissions.php", { data: { id } })
    permissions.value = permissions.value.filter(p => p.id !== id)
  } catch (error) {
    console.error("Error deleting permission:", error)
    alert("Failed to delete permission")
  }
}

const closeModal = () => {
  showModal.value = false
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

onMounted(() => {
  fetchPermissions()
  fetchModules()
})
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}
</style>